import React, { useState, useMemo } from 'react';
import { FaUnlink } from 'react-icons/fa';
import { Col, Row } from 'react-bootstrap';
import { cooperplaceApi } from 'services/api';
import Card from 'v3/components/Card';
import Text from 'v3/components/Text';
import Button from 'v3/components/Button';
import Fallback from 'v3/components/Fallback';
import DataTable from 'v3/components/DataTable';
import theme from 'v3/theme';
import { validatePermission } from 'actions';
import CostCentersManagedModal from './Modals/CostCenterManagedModal';
import { useUser } from './context';

const userDetailsTableStyles = {
  borderCollapse: 'collapse',
  '> thead > tr > th': {
    borderBottom: '1px solid #E5E7EB',
  },
  '> thead > tr > th:last-child': {
    textAlign: 'right',
    paddingRight: '24px',
  },
  '> tbody > tr': {
    borderBottom: '1px solid #E5E7EB',
  },
  '> tbody > tr > td:last-child': {
    textAlign: 'right',
    paddingRight: '24px',
  },
  '> tbody > tr:hover': {
    background: '#F5F6F8',
  },
  '> tbody > tr:hover > td': {
    background: '#F5F6F8',
  },
};

export function UserCostCentersManaged() {
  const user = useUser();
  const [search, setSearch] = useState('');
  const [perPage, setPerPage] = useState(5);
  const [page, setPage] = useState(1);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const hasPermissionToEditCostCenters = validatePermission('GESTAO_CENTRO_DE_CUSTO_USUARIOS');

  const costCentersManagedDataTable = useMemo(() => {
    const offset = (page - 1) * perPage;
    const costCentersManaged = user.data?.costCentersManaged
      ?.filter((item) => item.name.toLowerCase().includes(search.toLowerCase()))
      .slice(offset, perPage + offset);

    if (costCentersManaged?.length === 0 && page > 1) {
      setPage(page - 1);
    }
    return costCentersManaged;
  }, [user.data?.costCentersManaged, page, perPage, search]);

  function handleDeleteCostCenterManaged(costCentersManagedId) {
    user.setData({
      costCentersManaged: user.data?.costCentersManaged.filter(
        (costCenterManaged) => costCenterManaged.id !== costCentersManagedId,
      ),
    });
  }

  const isEditing = useMemo(() => {
    return !!user.data?.id;
  }, [user.data?.id]);

  async function handleDetachCostCenterManged(costCentersManagedId) {
    try {
      setLoading(true);
      await cooperplaceApi.delete(
        `users/${user.data.id}/deleteCostCenterManaged/${costCentersManagedId}`,
      );

      user.setData({
        costCentersManaged: user.data?.costCentersManaged.filter(
          (costCentersManaged) => costCentersManaged.id !== costCentersManagedId,
        ),
      });
    } catch (error) {
      user.showSnackbar('Erro ao excluir centro de custo', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  function handleRemoveCostCenterManaged(costCentersManagedId) {
    if (isEditing) handleDetachCostCenterManged(costCentersManagedId);
    else handleDeleteCostCenterManaged(costCentersManagedId);
  }

  return (
    <>
      <CostCentersManagedModal isOpen={show} onClose={() => setShow(false)} />
      <Card
        loading={user.isFetching}
        header={
          <Text type="header" color="dark">
            Centros de Custo Gerenciados
          </Text>
        }
        HeaderRightComponent={
          <>
            {hasPermissionToEditCostCenters && !!user.data?.costCentersManaged?.length ? (
              <Button
                onClick={() => {
                  user.updatePrevUser();
                  setShow(true);
                }}
                disabled={!user.hasPermissionToEdit}
              >
                <Text type="regular" weight="500" color="white">
                  Adicionar
                </Text>
              </Button>
            ) : null}
          </>
        }
      >
        <Row>
          <Fallback
            fall={!user.data?.costCentersManaged?.length}
            component={
              <Col xs={12} className="align-items-center d-flex flex-column my-2">
                <Text color={theme.colors.dark}>
                  Não há centros de custo gerenciados cadastrados nesse usuário.
                </Text>
                {hasPermissionToEditCostCenters ? (
                  <Button
                    className="mt-4"
                    onClick={() => {
                      user.updatePrevUser();
                      setShow(true);
                    }}
                    disabled={!user.hasPermissionToEdit}
                  >
                    <Text type="regular" weight="500" color="white">
                      Adicionar
                    </Text>
                  </Button>
                ) : null}
              </Col>
            }
          >
            <Col>
              <DataTable
                data={costCentersManagedDataTable}
                total={user.data?.costCentersManaged?.length}
                pages={Math.ceil(user.data?.costCentersManaged?.length / perPage)}
                currentPage={page}
                onPageChanged={setPage}
                onSearchChanged={setSearch}
                onPerPageChanged={(v) => setPerPage(+v)}
                loading={loading}
                searchPlaceholder="Nome"
                tableStyles={userDetailsTableStyles}
                columns={[
                  { name: 'Centro de Custo', selector: (row) => row.name },
                  {
                    name: 'Ação',
                    render: (row) => (
                      <Button
                        variant="error"
                        size="sm"
                        className="mx-1"
                        onClick={() => handleRemoveCostCenterManaged(row.id)}
                        title="remover centro de custo"
                        disabled={!hasPermissionToEditCostCenters || !user.hasPermissionToEdit}
                      >
                        <Text>
                          <FaUnlink />
                        </Text>
                      </Button>
                    ),
                  },
                ]}
              />
            </Col>
          </Fallback>
        </Row>
      </Card>
    </>
  );
}
